import { ISODateString } from 'models/misc'
import { Nullable } from 'models/helpers'
import { PlatformRole } from 'models/user'

/** Type of a conversation. Mirrors the backend `ConversationType`. */
export enum ConversationType {
  CLIENT_CREATIVE = 'CLIENT_CREATIVE',
  AI_SUPPORT = 'AI_SUPPORT',
  AGENT_CLIENT = 'AGENT_CLIENT',
}

/**
 * What an AI_SUPPORT conversation is about. Mirrors the backend `ConversationIntent`; only ever sent with
 * `ConversationType.AI_SUPPORT`.
 */
export enum ConversationIntent {
  ACCOUNT = 'ACCOUNT',
  OTHER = 'OTHER',
}

/** Lifecycle status of a conversation. Mirrors the backend `ConversationStatus`. */
export enum ConversationStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  ESCALATED = 'ESCALATED',
}

/** Role of a participant within a conversation. Mirrors the backend `ConversationParticipantRole`. */
export enum ConversationParticipantRole {
  CLIENT = 'CLIENT',
  CREATIVE = 'CREATIVE',
  AGENT = 'AGENT',
}

/** Type of a conversation message. Mirrors the backend `ConversationMessageType`. */
export enum ConversationMessageType {
  TEXT = 'TEXT',
  SYSTEM = 'SYSTEM',
  ATTACHMENT = 'ATTACHMENT',
}

/** Delivery status of a conversation message. Mirrors the backend `ConversationMessageStatus`. */
export enum ConversationMessageStatus {
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  READ = 'READ',
}

/** Origin of a conversation message. Mirrors the backend `ConversationMessageSource`. */
export enum ConversationMessageSource {
  PLATFORM = 'PLATFORM',
  SLACK = 'SLACK',
  SALESFORCE = 'SALESFORCE',
}

export enum LastMessageSenderKind {
  YOU = 'YOU',
  ASSISTANT = 'ASSISTANT',
  SUPPORT = 'SUPPORT',
  CREATIVE = 'CREATIVE',
  COLLEAGUE = 'COLLEAGUE',
}

export enum ConversationCategory {
  FEEDBACK = 'FEEDBACK',
  BILLING = 'BILLING',
  SCHEDULING = 'SCHEDULING',
  DELIVERY = 'DELIVERY',
  DOWNLOADS = 'DOWNLOADS',
  ACCOUNT = 'ACCOUNT',
  ORDERING = 'ORDERING',
  GENERAL = 'GENERAL',
}

export enum ConversationSupportStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_YOU = 'WAITING_FOR_YOU',
  RESOLVED = 'RESOLVED',
}

/** Presence state of a user, broadcast over WebSocket. Mirrors the backend `PresenceStatus`. */
export enum PresenceStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

/** Type of business entity a conversation is about. Mirrors the backend `ContextType` (extensible). */
export enum ContextType {
  ORDER = 'ORDER',
  PROPERTY = 'PROPERTY',
}

/**
 * A business context linked to a conversation (order, property, …). Rendered as a badge in the list/header.
 * `label` is a display string resolved by the backend (e.g. "Order #438593", "4 rue porte joie, 17138 Puilboreau").
 */
export interface ConversationContextDTO {
  type: ContextType
  id: string
  label?: string
}

/** A participant of a conversation. */
export interface ConversationParticipantDTO {
  userId: string
  /** Display name resolved from the user profile (backend-enriched); absent if unknown. */
  name?: string
  role: ConversationParticipantRole
  joinedAt: ISODateString
  leftAt?: ISODateString
}

/** Full conversation detail (e.g. from `GET /conversations/{id}`). */
export interface ConversationDTO {
  id: string
  type: ConversationType
  status: ConversationStatus
  sfCaseId?: string
  slackChannelId?: string
  participants: ConversationParticipantDTO[]
  /** Business contexts the conversation is about (order, property, …). */
  contexts?: ConversationContextDTO[]
  /** Subject/topic; null until set (later by the AI). */
  subject?: string
  /** Intent picked when an AI_SUPPORT conversation was opened from the guided flow; absent otherwise. */
  intent?: ConversationIntent | null
  language?: Nullable<string>
  createdAt: ISODateString
  updatedAt: ISODateString
  closedAt?: ISODateString
  reopenedAt?: Nullable<ISODateString>
}

/**
 * Conversation list item, enriched for display (from `GET /conversations`).
 * `title`, `lastMessagePreview`, `lastMessageAt` may be absent depending on backend availability —
 * the UI degrades gracefully (falls back to participant name, hides missing fields).
 */
export interface ConversationSummaryDTO {
  id: string
  type: ConversationType
  status: ConversationStatus
  sfCaseId?: string
  slackChannelId?: string
  participants: ConversationParticipantDTO[]
  /** Business contexts the conversation is about (order, property, …); rendered as badges. */
  contexts?: ConversationContextDTO[]
  /** Subject/topic of the conversation; null until set (later by the AI). Falls back to `title`. */
  subject?: string
  title?: string
  lastMessagePreview?: string
  lastMessageAt?: ISODateString
  lastMessageSenderId?: string
  lastMessageSource?: ConversationMessageSource
  lastMessageSenderKind?: LastMessageSenderKind
  lastMessageAttachmentCount?: number
  lastMessageAttachmentFilename?: Nullable<string>
  workedByAgent?: boolean
  category?: Nullable<ConversationCategory>
  supportStatus?: Nullable<ConversationSupportStatus>
  supportStatusChangedAt?: Nullable<ISODateString>
  unreadCount: number
  language?: Nullable<string>
  createdAt: ISODateString
  updatedAt: ISODateString
  closedAt?: ISODateString
  reopenedAt?: Nullable<ISODateString>
}

/**
 * A selectable suggestion the assistant can attach to a message — rendered in-thread as a card
 * (`order` | `property` | `visual` | `personalInfo` | `workspace`) or a quick-reply chip (`reply`).
 */
export interface MessageAction {
  kind: 'order' | 'property' | 'visual' | 'personalInfo' | 'workspace' | 'reply'
  /** What the action does (`TALK_TO_CREATIVE`, …). Server-defined, so left open rather than a union. */
  type?: string
  value: string
  label: string
  sublabel?: string
  status?: { label: string; tone: 'success' | 'pending' | 'neutral' }
  tags?: string[]
  thumbnail?: string
}

export interface ConversationAttachmentDTO {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  url: string | null
}

export interface AttachmentUploadRequestDTO {
  filename: string
  contentType: string
  sizeBytes: number
}

export interface AttachmentUploadUrlDTO {
  attachmentId: string
  uploadUrl: string
  headers: Record<string, string>
}

/** A persisted conversation message. */
export interface ConversationMessageDTO {
  id: string
  conversationId: string
  senderId: string
  /**
   * Role the sender holds in the conversation; absent when they are no longer a participant.
   * Combined with the conversation type it identifies the sender's kind — an AGENT in an AI_SUPPORT
   * conversation is the Backbone Assistant — so the UI never hardcodes a synthetic user id.
   */
  senderRole?: ConversationParticipantRole
  content: string
  clientMessageId: string
  type: ConversationMessageType
  status: ConversationMessageStatus
  source: ConversationMessageSource
  externalId?: string
  createdAt: ISODateString
  updatedAt: ISODateString
  deliveredAt?: ISODateString
  /** Selectable suggestion cards/chips the assistant attached to this message; absent on older messages. */
  actions?: MessageAction[]
  /**
   * Whether to offer the "did this resolve your question? can we close?" prompt under this assistant
   * answer. The UI shows it after a confident assistant answer; set `false` to suppress it (e.g. on a
   * low-confidence fallback). Optional; backend TBD.
   */
  closePrompt?: boolean
  senderName?: Nullable<string>
  attachments?: ConversationAttachmentDTO[]
  feedback?: Nullable<ConversationMessageFeedback>
}

/** A business context to attach when creating a conversation (the server resolves stakeholders). */
export interface CreateConversationContextDTO {
  type: ContextType
  id: string
}

/**
 * Body for `POST /conversations`.
 * Prefer `contexts` (guided, context-based flow — the server resolves the creative & derived contexts).
 * `participantIds` is the legacy direct path (one Client + one Creative).
 */
export interface CreateConversationDTO {
  type: ConversationType
  participantIds?: string[]
  contexts?: CreateConversationContextDTO[]
  /** AI_SUPPORT only — the backend rejects it on any other type. */
  intent?: ConversationIntent
  /** AI_SUPPORT only — asks for a fresh thread instead of get-or-create; the backend rejects it on any other type. */
  startNew?: boolean
}

/** Body for `POST /conversations/{conversationId}/messages`. */
export interface SendConversationMessageDTO {
  /** Textual content of the message (max 10,000 chars). */
  content: string
  /** Client-supplied idempotency key (same key + same sender returns the same message). */
  clientMessageId: string
  /** Kind of message; defaults to TEXT server-side when omitted. */
  type?: ConversationMessageType
  attachmentIds?: string[]
}

/** Body for `PUT /conversations/{conversationId}/messages/read`. */
export interface MarkConversationReadDTO {
  lastReadMessageId: string
}

/** Cursor-paginated response from `GET /conversations/{id}/messages`. Mirrors the backend `CursorPageDTO`. */
export interface CursorPageDTO<T> {
  items: T[]
  nextCursor?: string
  hasMore: boolean
  size: number
}

/** Direction of cursor pagination relative to the cursor. */
export enum CursorDirection {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

/** Real-time message broadcast on `/topic/conversations/{id}` (= "message:new"). */
export interface ConversationMessageBroadcastDTO {
  messageId: string
  conversationId: string
  senderId: string
  /** Role of the sender in the conversation; see `ConversationMessageDTO.senderRole`. */
  senderRole?: ConversationParticipantRole
  content: string
  clientMessageId: string
  messageType: ConversationMessageType
  status: ConversationMessageStatus
  source: ConversationMessageSource
  externalId?: string
  createdAt: ISODateString
  updatedAt: ISODateString
  /** See `ConversationMessageDTO.actions`. */
  actions?: MessageAction[]
  /** See `ConversationMessageDTO.closePrompt`. */
  closePrompt?: boolean
  senderName?: Nullable<string>
  attachments?: ConversationAttachmentDTO[]
  feedback?: Nullable<ConversationMessageFeedback>
}

/** Real-time presence update on `/topic/conversations/{id}` (= "presence:update"). */
export interface ConversationPresenceDTO {
  conversationId: string
  userId: string
  status: PresenceStatus
  at: ISODateString
}

/** Real-time read receipt on `/topic/conversations/{id}`. */
export interface ConversationReadReceiptDTO {
  conversationId: string
  userId: string
  lastReadMessageId: string
  readAt: ISODateString
}

/** Real-time typing indicator on `/topic/conversations/{id}`. */
export interface ConversationTypingDTO {
  conversationId: string
  userId: string
  isTyping: boolean
}

/** A contact the current user can start a conversation with (`GET /user/contacts/available`). */
export interface AvailableContactDTO {
  id: string
  name: string
  platformRole?: PlatformRole
  sfContactId?: string
}

export enum ConversationMessageFeedback {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
}

export enum MessageFeedbackOutcome {
  RECORDED = 'RECORDED',
  CLOSED = 'CLOSED',
  ESCALATED = 'ESCALATED',
  ALREADY_ESCALATED = 'ALREADY_ESCALATED',
}

export interface RateMessageDTO {
  rating: ConversationMessageFeedback
  confirmed: boolean
}

export interface MessageFeedbackResultDTO {
  rating: ConversationMessageFeedback
  outcome: MessageFeedbackOutcome
}

/** `CONVERSATION_CREATED` carries where to navigate; `ASSISTANT_TURN` answers in the open thread. */
export interface ExecuteActionResultDTO {
  result: 'CONVERSATION_CREATED' | 'ASSISTANT_TURN' | (string & {})
  conversationId?: string
}

export enum SupportDay {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export interface SupportHoursDTO {
  timezone: string
  days: SupportDay[]
  opensAt: string
  closesAt: string
  openNow: boolean
  nextOpeningAt: ISODateString
  closedSince: ISODateString | null
}
